/* Independent Features updates sequence. No shared hero assets or timers. */
if (!customElements.get('noor-updates')) {
  customElements.define('noor-updates', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.phone = this.querySelector('[data-updates-phone]');
      this.images = [...this.querySelectorAll('[data-updates-image]')];
      this.buttons = [...this.querySelectorAll('[data-updates-select]')];
      this.calm = matchMedia('(prefers-reduced-motion: reduce)');
      this.wide = matchMedia('(min-width: 990px)');
      this.index = 0;
      this.manual = false;
      this.ready = false;
      this.frame = 0;
      this.classList.add('is-enhanced');
      this.buttons.forEach((button, i) => button.addEventListener('click', () => {
        this.manual = true;
        this.paint(i);
      }, {signal}));
      let touch;
      this.phone.addEventListener('touchstart', e => {
        touch = {x: e.touches[0].clientX, y: e.touches[0].clientY};
      }, {passive: true, signal});
      this.phone.addEventListener('touchend', e => {
        if (!touch || this.scrolling) return;
        const dx = e.changedTouches[0].clientX - touch.x;
        const dy = e.changedTouches[0].clientY - touch.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          const rtl = getComputedStyle(this).direction === 'rtl';
          this.manual = true;
          this.paint(Math.max(0, Math.min(this.images.length - 1, this.index + ((dx < 0) !== rtl ? 1 : -1))));
        }
        touch = null;
      }, {passive: true, signal});
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      window.addEventListener('scroll', () => {
        if (Math.abs(window.scrollY - (this.lastScrollY ?? window.scrollY)) > 1) this.manual = false;
        this.lastScrollY = window.scrollY;
        this.schedule();
      }, {passive: true, signal});
      window.addEventListener('resize', () => this.measure(), {signal});
      this.calm.addEventListener('change', () => this.measure(), {signal});
      this.wide.addEventListener('change', () => this.measure(), {signal});
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.phone);
      this.resize.observe(this.firstElementChild);
      this.near = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.images.forEach(img => { img.loading = 'eager'; });
          Promise.all(this.images.map(img => img.decode().catch(() => {}))).then(() => {
            if (!this.isConnected) return;
            this.ready = this.images.every(img => img.naturalWidth > 0);
            this.measure();
          });
          this.near.disconnect();
        }
      }, {rootMargin: '800px'});
      this.near.observe(this);
      this.paint(0);
      this.measure();
      document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
    }
    measure() {
      const header = document.querySelector('#header-group');
      const headerHeight = header ? header.offsetHeight : 0;
      const viewport = window.innerHeight;
      const phoneHeight = this.phone.offsetHeight;
      const available = viewport - headerHeight - 48;
      const enabled = this.wide.matches && !this.calm.matches && available >= phoneHeight && this.ready;
      const changed = enabled !== this.scrolling;
      this.scrolling = enabled;
      this.classList.toggle('is-scroll', enabled);
      this.top = headerHeight + 24 + Math.max(0, (available - phoneHeight) / 2);
      this.style.setProperty('--updates-top', this.top + 'px');
      if (enabled) {
        const travel = Math.max(available, this.firstElementChild.offsetHeight - phoneHeight);
        this.style.setProperty('--updates-height', (phoneHeight + travel) + 'px');
      } else {
        this.style.removeProperty('--updates-height');
      }
      if (changed) { this.manual = false; this.paint(0); }
      this.schedule();
    }
    update() {
      if (!this.scrolling || this.manual) return;
      const rect = this.getBoundingClientRect();
      const distance = this.offsetHeight - this.phone.offsetHeight;
      const p = Math.max(0, Math.min(1, (this.top - rect.top) / Math.max(1, distance)));
      // Three reversible transitions; hold Device Settings for the final 15%.
      const transitions = [.15, .45, .75].map(start => Math.max(0, Math.min(1, (p - start) / .10)));
      this.paint(transitions.filter(value => value >= .5).length, [1, ...transitions]);
    }
    paint(index, opacity) {
      this.index = index;
      this.images.forEach((image, i) => {
        image.style.opacity = opacity ? opacity[i] : Number(i === index);
        image.setAttribute('aria-hidden', String(i !== index));
      });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.near?.disconnect();
      cancelAnimationFrame(this.frame);
      this.controller = null;
    }
  });
}
